<template>
  <section
    id="quiz-config"
    class="page-section quiz-section selection-section"
  >
    <div class="quiz-shell">
      <div class="quiz-content">

        <div v-if="!lockedMode" class="section-header">
          <p>Choose the experience mode, then configure content and scheduling.</p>
        </div>

        <div v-if="!lockedMode" class="quiz-mode-picker" role="tablist" aria-label="Experience mode">
          <button
            v-for="mode in experienceModes"
            :key="mode.id"
            type="button"
            class="quiz-mode-pill"
            :class="{ 'quiz-mode-pill--active': config.experienceMode === mode.id }"
            :aria-pressed="config.experienceMode === mode.id"
            @click="setExperienceMode(mode.id)"
          >
            <span class="quiz-mode-pill__label">{{ mode.label }}</span>
            <span class="quiz-mode-pill__desc">{{ mode.description }}</span>
          </button>
        </div>

        <div v-if="templatePickerEnabled" class="quiz-template-picker">
          <label class="quiz-theme-field">
            <span class="quiz-theme-field__icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 3a9 9 0 0 0 0 18" />
                <circle cx="7.5" cy="10.5" r="0.75" fill="currentColor" />
                <circle cx="12" cy="7.5" r="0.75" fill="currentColor" />
                <circle cx="16.5" cy="10.5" r="0.75" fill="currentColor" />
                <circle cx="15" cy="14.5" r="0.75" fill="currentColor" />
              </svg>
            </span>
            <span class="quiz-theme-field__label">Theme</span>
            <select
              class="quiz-tpl-select"
              :value="selectedTemplateFamilyId"
              @change="applyTemplate(availableTemplates.find(t => t.id === ($event.target as HTMLSelectElement).value)!)"
            >
              <option
                v-for="tpl in availableTemplates"
                :key="tpl.id"
                :value="tpl.id"
              >{{ tpl.name }}</option>
            </select>
          </label>
          <button
            v-if="libraryPickerEnabled"
            type="button"
            class="quiz-library-button"
            title="Pick an experience from your org's library"
            aria-label="Pick an experience from your org's library"
            @click="emit('open-library')"
          >
            <span class="quiz-library-button__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </span>
            <span class="quiz-library-button__label">Library</span>
          </button>
        </div>

        <div class="quiz-cards">
          <button
            type="button"
            class="quiz-card"
            :aria-label="settingsCardLabel"
            @click="showSettings = true"
          >
            <span class="quiz-card__title">{{ settingsCardLabel }}</span>
          </button>

          <button
            type="button"
            class="quiz-card"
            :aria-label="questionsCardLabel"
            @click="showQuestions = true"
          >
            <span class="quiz-card__title">{{ questionsCardLabel }}</span>
          </button>

          <button
            v-if="config.experienceMode === 'survey'"
            type="button"
            class="quiz-card"
            aria-label="Survey template style"
            @click="showSurveyTemplateModal = true"
          >
            <span class="quiz-card__title">Survey template — {{ surveyTemplateVariant === 'africa' ? 'Colour Palette' : 'Default' }}</span>
          </button>

          <!-- Distribution Schedule card -->
          <button
            v-if="scheduleEditorEnabled"
            type="button"
            class="quiz-card"
            aria-label="Configure question distribution schedule"
            @click="showSchedule = true"
          >
            <div class="quiz-card__icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <line x1="8" y1="14" x2="8" y2="14" stroke-linecap="round" stroke-width="3"/>
                <line x1="12" y1="14" x2="12" y2="14" stroke-linecap="round" stroke-width="3"/>
                <line x1="16" y1="14" x2="16" y2="14" stroke-linecap="round" stroke-width="3"/>
                <line x1="8" y1="18" x2="8" y2="18" stroke-linecap="round" stroke-width="3"/>
                <line x1="12" y1="18" x2="12" y2="18" stroke-linecap="round" stroke-width="3"/>
              </svg>
            </div>
            <div class="quiz-card__body">
              <span class="quiz-card__title">Distribution Schedule</span>
              <span class="quiz-card__meta">
                <template v-if="config.slots.length <= 1">
                  All questions available at campaign start
                </template>
                <template v-else>
                  {{ config.slots.length }} slots · {{ scheduleSummary }}
                </template>
              </span>
            </div>
            <span class="quiz-card__arrow" aria-hidden="true">›</span>
          </button>
        </div>

      </div>
    </div>

    <QuizSettingsModal
      :show="showSettings"
      :config="config"
      :max-pass-threshold="Math.max(totalQuestions, 1)"
      :experience-type="settingsExperienceType"
      @close="showSettings = false"
      @save="handleSettingsSave"
      @attempt-window-change="handleAttemptWindowChange"
    />

    <QuizQuestionsModal
      :show="showQuestions"
      :rounds="config.rounds"
      :experience-mode="config.experienceMode || 'quiz'"
      @close="showQuestions = false"
      @save="handleQuestionsSave"
    />

    <QuizScheduleModal
      v-if="scheduleEditorEnabled"
      :show="showSchedule"
      :slots="config.slots"
      :rounds="config.rounds"
      :campaign-schedule="campaignSchedule"
      :no-active-slot-action="config.noActiveSlotAction ?? null"
      @close="showSchedule = false"
      @save="handleScheduleSave"
    />

    <SurveyTemplateModal
      :show="showSurveyTemplateModal"
      :selected="surveyTemplateVariant"
      @select="onSelectSurveyTemplate"
      @close="showSurveyTemplateModal = false"
    />
  </section>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch, onBeforeUnmount } from 'vue'
import type { QuizConfig, QuizNoActiveSlotAction, QuizRound, QuizSlot } from '@/types/form.types'

export interface QuizCampaignScheduleInput {
  startDateTime: string | Date | null
  endDateTime: string | Date | null
  timezone?: string | null
}
import { useUIStore } from '@/store/ui.store'
import QuizSettingsModal from '@/components/QuizSettingsModal.vue'
import QuizQuestionsModal from '@/components/QuizQuestionsModal.vue'
import QuizScheduleModal from '@/components/QuizScheduleModal.vue'
import SurveyTemplateModal from '@/components/SurveyTemplateModal.vue'
import {
  getDefaultTemplateIdForExperienceMode,
  getTemplatesForExperienceMode,
  DEFAULT_TEMPLATE_FAMILY_ID,
  buildDefaultTemplateRules,
  buildQuizConfigFromTemplate,
  QUIZ_TEMPLATES,
  SURVEY_TEMPLATES,
} from '@/config/quiz-templates.config'
import type { QuizTemplateDefinition } from '@/config/quiz-templates.config'
import { renameRoundsForWindow, renameSlotsForWindow } from '@/utils/roundLabels'
import { clampQuestionsPerRound } from '@/utils/quizSettings'

const experienceModes = [
  { id: 'quiz', label: 'Quiz', description: 'Scored questions with correct answers.' },
  { id: 'survey', label: 'Survey', description: 'Opinion-based questions and free-text feedback.' },
  { id: 'all-in-one', label: 'All-In-One', description: 'Mix quiz and survey stages in one flow.' },
] as const

const uiStore = useUIStore()

const props = defineProps<{
  modelValue: QuizConfig
  campaignSchedule: QuizCampaignScheduleInput
  experienceType?: 'quiz' | 'survey' | null
  showTemplatePicker?: boolean
  showScheduleEditor?: boolean
  summaryContext?: 'campaign' | 'experience'
  showLibraryPicker?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: QuizConfig): void
  (e: 'open-library'): void
}>()

function deepCloneRounds(rounds: QuizRound[]): QuizRound[] {
  return rounds.map(r => ({
    ...r,
    stages: (r.stages ?? []).map(stage => ({
      ...stage,
      type: stage.type || 'quiz',
      questions: stage.questions.map(q => ({
        ...q,
        choices: q.choices.map(c => ({ ...c })),
      })),
    })),
  }))
}

function deepCloneSlots(slots: QuizSlot[] = []): QuizSlot[] {
  return slots.map(slot => ({ ...slot }))
}

function buildDefaultSlotForRound(round: QuizRound, index: number): QuizSlot {
  return {
    id: `slot-${round.id || `round-${index + 1}`}`,
    label: round.label || `Round ${index + 1}`,
    roundId: round.id || `round-${index + 1}`,
    releaseType: index === 0 ? 'immediate' : 'offset',
    offsetDays: index === 0 ? undefined : index * 7,
  }
}

function reconcileSlotsWithRounds(rounds: QuizRound[], slots: QuizSlot[] = []): QuizSlot[] {
  const slotByRoundId = new Map(slots.map((slot) => [slot.roundId, { ...slot }]))
  return rounds.map((round, index) => {
    const existing = slotByRoundId.get(round.id)
    if (existing) {
      return {
        ...existing,
        id: existing.id || `slot-${round.id}`,
        label: existing.label || round.label || `Round ${index + 1}`,
        roundId: round.id,
      }
    }
    return buildDefaultSlotForRound(round, index)
  })
}

function normalizeQuizConfig(value: QuizConfig): QuizConfig {
  const legacyRounds = (value.rounds ?? []) as Array<QuizRound & { questions?: any[]; releaseType?: string; offsetDays?: number; releaseDate?: string }>
  const rounds = legacyRounds.map((round, index) => ({
    id: round.id || `round-${index + 1}`,
    label: round.label || `Round ${index + 1}`,
    stages: round.stages?.length
      ? round.stages
        .map((stage, stageIndex) => ({
          ...stage,
          order: stageIndex + 1,
          type: (stage.type || (value.experienceMode === 'survey' ? 'survey' : 'quiz')) as 'quiz' | 'survey',
          questions: (stage.questions ?? []).map((question) => ({
            ...question,
            choices: (question.choices ?? []).map((choice: any) => ({ ...choice })),
          })),
        }))
      : [{
          id: `${round.id || `round-${index + 1}`}-stage-1`,
          order: 1,
          type: (value.experienceMode === 'survey' ? 'survey' : 'quiz') as 'quiz' | 'survey',
          questions: (round.questions ?? []).map((question) => ({
            ...question,
            choices: (question.choices ?? []).map((choice: any) => ({ ...choice })),
          })),
        }],
  }))

  const sourceSlots = value.slots?.length
    ? value.slots
    : legacyRounds.map((round, index) => ({
        id: `slot-${round.id || `round-${index + 1}`}`,
        label: round.label || `Round ${index + 1}`,
        roundId: round.id || `round-${index + 1}`,
        releaseType: (round.releaseType as QuizSlot['releaseType']) || (index === 0 ? 'immediate' : 'offset'),
        offsetDays: round.offsetDays,
        releaseDate: round.releaseDate,
        releaseTime: (round as any).releaseTime,
      }))
  const slots = reconcileSlotsWithRounds(rounds, sourceSlots)

  return {
    ...value,
    quizTemplateId: value.quizTemplateId || (props.showTemplatePicker === false ? undefined : getDefaultTemplateIdForExperienceMode(value.experienceMode)),
    experienceTemplateFamilyId: value.experienceTemplateFamilyId,
    experienceTemplateVersionId: value.experienceTemplateVersionId,
    experienceDefinitionId: value.experienceDefinitionId,
    experienceDefinitionVersionId: value.experienceDefinitionVersionId,
    leaderboard: value.leaderboard
      ? {
          enabled: value.leaderboard.enabled === true,
          eventName: value.leaderboard.eventName || 'quiz.completed.score',
          displayMode: 'top-10-plus-user',
          title: value.leaderboard.title || 'Top Players',
          emptyStateText: value.leaderboard.emptyStateText || 'Be the first to post a score.',
          showAfterCompletionOnly: value.leaderboard.showAfterCompletionOnly !== false,
        }
      : {
          enabled: false,
          eventName: 'quiz.completed.score',
          displayMode: 'top-10-plus-user',
          title: 'Top Players',
          emptyStateText: 'Be the first to post a score.',
          showAfterCompletionOnly: true,
        },
    rounds,
    slots: slots.map((slot, index) => ({
      ...slot,
      id: slot.id || `slot-${slot.roundId || `round-${index + 1}`}`,
      label: slot.label || `Slot ${index + 1}`,
    })),
  }
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 9)
}

const lockedMode = computed(() => {
  if (props.experienceType === 'quiz' || props.experienceType === 'survey') {
    return props.experienceType
  }
  return null
})

const config = reactive<QuizConfig>({
  ...normalizeQuizConfig(props.modelValue),
  rounds: deepCloneRounds(normalizeQuizConfig(props.modelValue).rounds ?? []),
  slots: deepCloneSlots(normalizeQuizConfig(props.modelValue).slots ?? []),
})

watch(() => props.modelValue, (val) => {
  const normalized = normalizeQuizConfig(val)
  Object.assign(config, normalized)
  config.rounds = deepCloneRounds(normalized.rounds ?? [])
  config.slots = deepCloneSlots(normalized.slots ?? [])
}, { deep: true })

watch(lockedMode, (mode) => {
  if (mode && config.experienceMode !== mode) {
    setExperienceMode(mode)
  }
}, { immediate: true })

const showSettings = ref(false)
const showSurveyTemplateModal = ref(false)
// Survey renderer skin (additive). Reads/writes quizConfig.surveyTemplateVariant
// via the reactive config + emitUpdate. Default 'classic' keeps the current
// blue template; 'africa' = the new colour-palette tile template.
const surveyTemplateVariant = computed<'classic' | 'africa'>(() =>
  config.surveyTemplateVariant === 'africa' ? 'africa' : 'classic',
)
function onSelectSurveyTemplate(variant: 'classic' | 'africa') {
  config.surveyTemplateVariant = variant
  emitUpdate()
  showSurveyTemplateModal.value = false
}
const showQuestions = ref(false)
const showSchedule = ref(false)

watch(showSettings, (isOpen) => {
  if (isOpen) uiStore.openModal()
  else uiStore.closeModal()
})

watch(showQuestions, (isOpen) => {
  if (isOpen) uiStore.openModal()
  else uiStore.closeModal()
})

watch(showSchedule, (isOpen) => {
  if (isOpen) uiStore.openModal()
  else uiStore.closeModal()
})

onBeforeUnmount(() => {
  if (showSettings.value) uiStore.closeModal()
  if (showQuestions.value) uiStore.closeModal()
  if (showSchedule.value) uiStore.closeModal()
})

const templatePickerEnabled = computed(() => props.showTemplatePicker !== false)
const libraryPickerEnabled = computed(() => props.showLibraryPicker !== false)
const scheduleEditorEnabled = computed(() => props.showScheduleEditor !== false)

watch(scheduleEditorEnabled, (enabled) => {
  if (!enabled) {
    showSchedule.value = false
  }
}, { immediate: true })

const totalQuestions = computed(() =>
  config.rounds.reduce((sum, r) => sum + r.stages.reduce((stageSum, stage) => stageSum + stage.questions.length, 0), 0)
)

const totalStages = computed(() =>
  config.rounds.reduce((sum, round) => sum + round.stages.length, 0),
)

const validQuestionCount = computed(() =>
  config.rounds.flatMap(r => r.stages.flatMap(stage => stage.questions.filter((question) => isQuestionValid(question, stage.type)))).length,
)

const scheduleSummary = computed(() => {
  const scheduled = config.slots.filter(slot => slot.releaseType !== 'immediate')
  if (scheduled.length === 0) return 'Immediate release'
  const maxOffset = config.slots
    .filter(slot => slot.releaseType === 'offset' && slot.offsetDays)
    .map(slot => slot.offsetDays ?? 0)
    .reduce((a, b) => Math.max(a, b), 0)
  if (maxOffset > 0) return `Spanning ${maxOffset} days`
  return `${scheduled.length} scheduled`
})

const SYNTHETIC_DEFAULT_TEMPLATE: QuizTemplateDefinition = {
  id: DEFAULT_TEMPLATE_FAMILY_ID,
  name: 'Default (no theme)',
  description: 'Empty experience with sensible default rules. Pick a theme to pre-populate questions.',
  emoji: '⚙️',
  defaultColors: {
    primaryColor: '#9B6DFF',
    backgroundColor: '#050614',
    correctColor: '#39FF14',
    incorrectColor: '#FF3131',
  },
  defaultSettings: {
    timeLimitSeconds: 60,
    passThreshold: 1,
    attemptLimit: 1,
    attemptWindow: 'day',
  },
  defaultRounds: [],
  defaultSlots: [],
}

const availableTemplates = computed(() => [
  SYNTHETIC_DEFAULT_TEMPLATE,
  ...getTemplatesForExperienceMode(config.experienceMode),
])

const selectedTemplateFamilyId = computed(() => {
  if (config.experienceTemplateFamilyId) return config.experienceTemplateFamilyId
  if (config.quizTemplateId) return config.quizTemplateId
  if (templatePickerEnabled.value) return getDefaultTemplateIdForExperienceMode(config.experienceMode)
  return ''
})

const selectedTemplateFamilyName = computed(() =>
  availableTemplates.value.find((template) => template.id === selectedTemplateFamilyId.value)?.name || '',
)

const experienceTypeLabel = computed(() => {
  if (config.experienceMode === 'all-in-one') return 'All-In-One'
  if (config.experienceMode === 'survey') return 'Survey'
  return 'Quiz'
})
const summaryContextLabel = computed(() =>
  props.summaryContext === 'experience'
    ? 'saved into this experience'
    : 'auto-saved into this campaign',
)

const defaultExperienceName = computed(() => `${experienceTypeLabel.value} Experience`)

function applyTemplate(tpl: QuizTemplateDefinition) {
  // Synthetic "Default" family: apply canonical default rules + empty rounds.
  // Distinct from any pre-populated theme template.
  if (tpl.id === DEFAULT_TEMPLATE_FAMILY_ID) {
    const rules = buildDefaultTemplateRules()
    config.quizTemplateId = DEFAULT_TEMPLATE_FAMILY_ID
    config.experienceTemplateFamilyId = DEFAULT_TEMPLATE_FAMILY_ID
    config.experienceTemplateVersionId = undefined
    config.timeLimitSeconds = rules.timeLimitSeconds
    config.passThreshold = rules.passThreshold
    config.attemptLimit = rules.attemptLimit
    config.attemptWindow = rules.attemptWindow
    config.attemptWindowRolloverTime = rules.attemptWindowRolloverTime
    config.attemptWindowRolloverWeekday = rules.attemptWindowRolloverWeekday
    config.leaderboard = {
      enabled: rules.leaderboardEnabled,
      eventName: 'quiz.completed.score',
      displayMode: 'top-10-plus-user',
      title: 'Top Players',
      emptyStateText: 'Be the first to post a score.',
      showAfterCompletionOnly: true,
    }
    config.rounds = []
    config.slots = []
    materializeCampaignExperience(`Default ${experienceTypeLabel.value}`)
    emitUpdate()
    return
  }

  config.quizTemplateId = tpl.id
  config.experienceTemplateFamilyId = tpl.id
  config.experienceTemplateVersionId = undefined
  config.primaryColor = tpl.defaultColors.primaryColor
  config.backgroundColor = tpl.defaultColors.backgroundColor
  config.correctColor = tpl.defaultColors.correctColor
  config.incorrectColor = tpl.defaultColors.incorrectColor
  config.timeLimitSeconds = tpl.defaultSettings.timeLimitSeconds
  config.passThreshold = tpl.defaultSettings.passThreshold
  config.attemptLimit = tpl.defaultSettings.attemptLimit
  config.attemptWindow = tpl.defaultSettings.attemptWindow
  config.rounds = tpl.defaultRounds.map(r => ({
      ...r,
      stages: r.stages.map(stage => ({
        ...stage,
        type: (stage as any).type || (config.experienceMode === 'survey' ? 'survey' : 'quiz'),
        questions: stage.questions.map(q => ({
          ...q,
          choices: q.choices.map(c => ({ ...c })),
      })),
    })),
  }))
  config.slots = tpl.defaultSlots.map(slot => ({ ...slot }))
  materializeCampaignExperience(`${tpl.name} ${experienceTypeLabel.value}`)
  emitUpdate()
}

function isQuestionValid(question: QuizRound['stages'][number]['questions'][number], stageType?: 'quiz' | 'survey') {
  const responseType = question.type === 'quiz' ? 'single-select' : (question.type || (stageType === 'survey' ? 'single-select' : 'single-select'))
  const promptFormat = question.promptFormat || ((question.prompt?.videoAssetId && 'video') || (question.prompt?.imageAssetId && 'image') || 'text')
  if (!question.text.trim()) return false
  if (promptFormat === 'image' && !question.prompt?.imageAssetId?.trim()) return false
  if (promptFormat === 'video' && !question.prompt?.videoAssetId?.trim()) return false
  if (responseType === 'free-text') return true
  if (responseType === 'rating') return Boolean((question.ratingScale?.min ?? 1) && (question.ratingScale?.max ?? 5))
  if (responseType === 'boolean') return true
  const hasValidChoices = question.choices.length >= 2 && question.choices.every(c => c.label.trim() || c.imageAssetId?.trim())
  if (!hasValidChoices) return false
  if (responseType === 'multi-select') return Boolean(question.correctChoiceIds?.length || stageType === 'survey')
  if (config.experienceMode === 'survey' || stageType === 'survey') return true
  return Boolean(question.correctChoiceId)
}

function signatureOfRounds(rounds: QuizRound[] | undefined): string {
  return (rounds ?? [])
    .map((round) =>
      (round.stages ?? [])
        .map((stage) =>
          (stage.questions ?? [])
            .map((question) =>
              `${question.text ?? ''}|${(question.choices ?? [])
                .map((choice) => choice.label ?? '')
                .join(',')}`,
            )
            .join('||'),
        )
        .join('|||'),
    )
    .join('||||')
}

function roundsMatchAnyTemplateSeed(rounds: QuizRound[] | undefined): boolean {
  if (!rounds || rounds.length === 0) return true
  const sig = signatureOfRounds(rounds)
  for (const tpl of [...QUIZ_TEMPLATES, ...SURVEY_TEMPLATES]) {
    if (signatureOfRounds(tpl.defaultRounds as unknown as QuizRound[]) === sig) return true
  }
  return false
}

function setExperienceMode(mode: QuizConfig['experienceMode']) {
  if (config.experienceMode === mode) return
  config.experienceMode = mode

  const nextTemplates = getTemplatesForExperienceMode(mode)
  if (!nextTemplates.some((template) => template.id === config.quizTemplateId)) {
    config.quizTemplateId = getDefaultTemplateIdForExperienceMode(mode)
  }
  config.experienceTemplateFamilyId = config.quizTemplateId
  config.experienceTemplateVersionId = undefined

  if (roundsMatchAnyTemplateSeed(config.rounds)) {
    const fresh = buildQuizConfigFromTemplate(config.quizTemplateId, mode)
    if (fresh) {
      config.rounds = fresh.rounds
      config.slots = fresh.slots
      config.passThreshold = fresh.passThreshold
      config.timeLimitSeconds = fresh.timeLimitSeconds
      config.attemptLimit = fresh.attemptLimit
      config.attemptWindow = fresh.attemptWindow
      config.leaderboard = fresh.leaderboard
    }
  } else {
    config.rounds = config.rounds.map((round) => ({
      ...round,
      stages: round.stages.map((stage) => ({
        ...stage,
        type: mode === 'survey' ? 'survey' : (mode === 'all-in-one' ? (stage.type || 'quiz') : 'quiz'),
        questions: stage.questions.map((question) =>
          mode === 'survey'
            ? { ...question, correctChoiceId: undefined, correctChoiceIds: undefined }
            : question,
        ),
      })),
    }))
    if (mode === 'survey') {
      config.passThreshold = 0
      if (config.leaderboard?.eventName?.startsWith('quiz.')) {
        config.leaderboard = { ...config.leaderboard, eventName: 'survey.completed' }
      }
    }
  }

  materializeCampaignExperience(`${mode === 'all-in-one' ? 'All-In-One' : mode === 'survey' ? 'Survey' : 'Quiz'} Experience`)
  emitUpdate()
}

function materializeCampaignExperience(preferredName?: string) {
  if (!config.experienceDefinitionLocalKey) {
    config.experienceDefinitionLocalKey = `exp-${generateId()}`
  }
  if (!config.experienceDefinitionName?.trim()) {
    config.experienceDefinitionName = preferredName || defaultExperienceName.value
  }
  config.experienceDefinitionStatus = 'published'
  config.experienceDefinitionLastSavedAt = new Date().toISOString()
  config.experienceDefinitionId = undefined
  config.experienceDefinitionVersionId = undefined
}

function emitUpdate() {
  emit('update:modelValue', JSON.parse(JSON.stringify(config)))
}

function handleSettingsSave(payload: {
  timeLimitSeconds: number
  passThreshold: number
  attemptLimit: number
  attemptWindow: QuizConfig['attemptWindow']  // 'week' | 'day' | 'hour' | 'minute'
  attemptWindowRolloverTime: string
  attemptWindowRolloverWeekday: number
  questionsPerRound: number
  leaderboard: NonNullable<QuizConfig['leaderboard']>
  primaryColor: string
  backgroundColor: string
  correctColor: string
  incorrectColor: string
}) {
  config.timeLimitSeconds = payload.timeLimitSeconds
  config.passThreshold = payload.passThreshold
  config.attemptLimit = payload.attemptLimit
  config.attemptWindow = payload.attemptWindow
  config.attemptWindowRolloverTime = payload.attemptWindowRolloverTime
  config.attemptWindowRolloverWeekday = payload.attemptWindowRolloverWeekday
  config.questionsPerRound = clampQuestionsPerRound(payload.questionsPerRound)
  config.leaderboard = { ...payload.leaderboard }
  config.primaryColor = payload.primaryColor
  config.backgroundColor = payload.backgroundColor
  config.correctColor = payload.correctColor
  config.incorrectColor = payload.incorrectColor
  showSettings.value = false
  emitUpdate()
}

/**
 * Theme-driven round label rename — fires when the creator changes the
 * Attempt Window in the settings modal. Defaults are rewritten; manual edits
 * are preserved (see utils/roundLabels.ts).
 */
function handleAttemptWindowChange(payload: {
  previous: QuizConfig['attemptWindow']
  next: QuizConfig['attemptWindow']
}) {
  if (payload.previous === payload.next) return
  config.rounds = renameRoundsForWindow(config.rounds, payload.previous, payload.next) as typeof config.rounds
  config.slots = renameSlotsForWindow(config.slots, payload.previous, payload.next) as typeof config.slots
  emitUpdate()
}

/**
 * Settings modal needs to know whether to render Survey-only / Quiz-only /
 * Game placeholder fields. Mirrors `experienceMode` but maps 'all-in-one' →
 * 'quiz' (full field surface) and falls back to 'quiz' when unset.
 */
const settingsExperienceType = computed<'quiz' | 'survey' | 'game'>(() => {
  if (config.experienceMode === 'survey') return 'survey'
  return 'quiz'
})

const settingsCardLabel = computed(() => {
  if (config.experienceMode === 'survey') return 'Edit survey settings'
  if (config.experienceMode === 'all-in-one') return 'Edit experience settings'
  return 'Edit quiz configuration'
})

const questionsCardLabel = computed(() => {
  if (config.experienceMode === 'survey') return 'Edit survey questions'
  if (config.experienceMode === 'all-in-one') return 'Edit experience questions'
  return 'Edit quiz questions and rounds'
})

function handleQuestionsSave(rounds: QuizRound[]) {
  config.rounds = rounds
  config.slots = reconcileSlotsWithRounds(rounds, config.slots)
  showQuestions.value = false
  emitUpdate()
}

function handleScheduleSave(payload: { slots: QuizSlot[]; noActiveSlotAction: QuizNoActiveSlotAction | null }) {
  config.slots = payload.slots
  config.noActiveSlotAction = payload.noActiveSlotAction
  showSchedule.value = false
  emitUpdate()
}

defineExpose({
  openQuestions: () => {
    showQuestions.value = true
  },
})
</script>

<style scoped>
.quiz-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none !important;
}

.quiz-section > .quiz-shell {
  width: 100%;
  max-width: none;
  margin-inline: 0;
}

.quiz-shell {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
}

.quiz-content {
  width: min(100%, var(--base-layout-section-max-width));
  max-width: 38rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--section-content-gap, 1.25rem);
}

.quiz-mode-picker {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
}

.quiz-mode-pill {
  display: grid;
  gap: 0.2rem;
  padding: 0.8rem 0.9rem;
  border-radius: var(--radius-md, 1rem);
  border: 1px solid var(--color-border, rgba(var(--vt-c-accent-rgb), 0.2));
  background: rgba(255, 255, 255, 0.03);
  color: var(--color-text, #fff);
  text-align: center;
  cursor: pointer;
}

.quiz-mode-pill--active {
  border-color: var(--color-primary, #9b6dff);
  background: rgba(155, 109, 255, 0.1);
}

.quiz-mode-pill__label {
  font-weight: 700;
  font-size: 0.9rem;
}

.quiz-mode-pill__desc {
  font-size: 0.72rem;
  color: var(--color-text-muted, rgba(255,255,255,0.6));
  line-height: 1.3;
}

/* Template picker — theme selector (A2). Visually distinct from
   Configuration / Questions action pills below: pastel-green ring, dark-blue
   text, "Theme" label inline so it reads as a selector, not a button. */
.quiz-template-picker {
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: 0.4rem;
  flex-wrap: nowrap;
}

.quiz-theme-field {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem 0.55rem 0.85rem;
  border-radius: 999px;
  border: 1.5px solid var(--vt-c-accent);
  background: rgba(57, 255, 20, 0.06);
  color: #0a1628;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
}

.quiz-theme-field:hover,
.quiz-theme-field:focus-within {
  background: rgba(57, 255, 20, 0.12);
  border-color: var(--vt-c-accent);
}

.quiz-theme-field__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  color: var(--vt-c-accent);
  flex-shrink: 0;
}

.quiz-theme-field__label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vt-c-text-muted, rgba(255, 255, 255, 0.7));
  flex-shrink: 0;
}

.quiz-tpl-select {
  flex: 1;
  min-width: 0;
  padding: 0.4rem 0.5rem;
  border-radius: 0.5rem;
  border: none;
  background: var(--vt-c-accent);
  color: #0a1628;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  appearance: auto;
  text-align: left;
  text-align-last: left;
}

.quiz-tpl-select:focus-visible {
  outline: 2px solid var(--vt-c-accent);
  outline-offset: 2px;
}

/* "Library" button — sits next to the Theme dropdown so the creator can
   choose between picking a published experience from the org library or
   continuing to configure from a theme template. Matches the theme pill's
   height and ring colour so the two read as a paired control. */
.quiz-library-button {
  /* Override the global button{} reset (width:100%, height:3.125rem, margin-top, gradient bg). */
  width: auto;
  height: auto;
  margin: 0;
  box-shadow: none;
  overflow: visible;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0 0.85rem;
  align-self: stretch;
  border-radius: 999px;
  border: 1.5px solid var(--vt-c-accent);
  background: rgba(57, 255, 20, 0.06);
  color: var(--vt-c-accent);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease;
}

.quiz-library-button:hover,
.quiz-library-button:focus-visible {
  background: var(--vt-c-accent);
  color: #0a1628;
  outline: none;
}

.quiz-library-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
}

/* At narrow widths the label hides — icon + tooltip remains. Keeps the
   theme dropdown legible when the column is tight. */
@media (max-width: 420px) {
  .quiz-library-button {
    padding: 0 0.6rem;
  }
  .quiz-library-button__label {
    display: none;
  }
}

/* Card grid */
.quiz-cards {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Individual card-button */
.quiz-card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.85rem 1.15rem;
  border-radius: var(--base-layout-button-border-radius-lg, 1rem);
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: #0a1628;
  cursor: pointer;
  text-align: center;
  transition: background 150ms ease;
  margin: 0;
  height: auto;
  min-height: unset;
  box-shadow: none;
}

.quiz-card:hover {
  background: var(--ui-inverse-cta-bg-hover);
}

.quiz-card__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0a1628;
  line-height: 1.25;
}

/* Colour swatches */
.quiz-card__swatches {
  display: flex;
  gap: 0.3rem;
  flex-shrink: 0;
}

.quiz-card__swatch {
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

/* Arrow chevron */
.quiz-card__arrow {
  flex-shrink: 0;
  font-size: 1.5rem;
  font-weight: 300;
  color: var(--vt-c-text-muted, rgba(255, 255, 255, 0.5));
  line-height: 1;
  margin-left: 0.25rem;
}

@media (max-width: 767px) {
  .quiz-content {
    max-width: 100%;
    padding: 0 0.25rem;
  }

  .quiz-card {
    padding: 0.85rem 1rem;
    gap: 0.7rem;
  }

  .quiz-card__icon {
    width: 2.35rem;
    height: 2.35rem;
  }

  .quiz-card__swatches {
    display: none;
  }
}

@media (min-width: 768px) and (max-height: 900px) {
  .quiz-content {
    gap: 0.9rem;
  }

  .quiz-mode-picker,
  .quiz-cards {
    gap: 0.55rem;
  }

  .quiz-card {
    padding: 0.85rem 1rem;
  }
}
</style>
